import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { X, Mail, Bell, Check } from 'lucide-react';
import * as Dialog from '@radix-ui/react-dialog';
import { subscribeEmail } from '../services/subscriberApi';

const NotifyMeModal = ({ isOpen, onClose }) => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      setError('Please enter your email address');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      // Subscribe email using Supabase API
      const result = await subscribeEmail(email, 'notify_modal');
      
      if (result.success) {
        setIsSuccess(true);
        setTimeout(() => {
          setIsSuccess(false);
          setEmail('');
          onClose();
        }, 2000);
      } else {
        setError(result.message);
      }
    } catch (err) {
      console.error('Subscription error:', err);
      setError('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setEmail('');
      setError('');
      setIsSuccess(false);
      onClose();
    }
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={handleClose}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/70 z-[100]" />
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
          <Dialog.Content className="w-full max-w-md bg-[#141414] border border-white/20 rounded-2xl p-4 md:p-6 max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-4 md:mb-6">
            <div className="flex items-center gap-2 md:gap-3">
              <div className="p-2 bg-primary-purple/20 rounded-full">
                <Bell className="w-4 h-4 md:w-5 md:h-5 text-primary-purple" />
              </div>
              <h2 className="text-lg md:text-xl font-semibold text-white">Stay Updated</h2>
            </div>
            <Dialog.Close asChild>
              <button 
                className="p-2 hover:bg-white/10 rounded-full transition-colors touch-manipulation"
                disabled={isSubmitting}
              >
                <X className="w-5 h-5 text-white/60" />
              </button>
            </Dialog.Close>
          </div>

          {isSuccess ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center py-8"
            >
              <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-green-500" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">You're All Set!</h3>
              <p className="text-white/80">
                We'll notify you about new Nollywood releases and platform updates.
              </p>
            </motion.div>
          ) : (
            <>
              <p className="text-white/80 mb-4 md:mb-6 text-sm md:text-base">
                Get notified about the latest Nollywood movies and platform updates. 
                No spam, just the good stuff.
              </p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-white/90 mb-2">
                    Email Address
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-white/40" />
                    <input
                      type="email"
                      id="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your email"
                      className="w-full bg-white/10 border border-white/20 rounded-xl pl-12 pr-4 py-3 md:py-3 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-primary-purple/50 focus:border-primary-purple transition-all duration-300 text-base"
                      disabled={isSubmitting}
                      autoComplete="email"
                      inputMode="email"
                    />
                  </div>
                  {error && (
                    <p className="text-red-400 text-sm mt-2">{error}</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full px-4 py-3 rounded-xl bg-white text-black font-semibold hover:bg-white/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 touch-manipulation"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                      Subscribing...
                    </>
                  ) : (
                    <>
                      <Bell className="w-4 h-4" />
                      Notify Me
                    </>
                  )}
                </button>
              </form>

              <p className="text-xs text-white/60 mt-4 text-center leading-relaxed">
                By subscribing, you agree to receive updates about new movies and platform features.
              </p>
            </>
          )}
          </Dialog.Content>
        </div>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default NotifyMeModal;
